import { ChevronLeft, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button, Card } from "../../../../components";
import PageHeader from "../../../../components/common/PageHeader";
import {
  nriBuildingBlocks,
  nriImpactGoals,
  nriOutcomeChecks,
  nriPriorityAreas,
} from "../data/nriImpactReport.data";

export default function NriImpactReportPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-full bg-[#effbf3] px-4 py-6 text-[#004b3a] sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-6">
        <PageHeader
          title="Are residents in NRI neighborhoods better off?"
          subtitle={
            <span>
              <span className="font-bold uppercase tracking-[0.12em] text-[#008a72]">
                Neighborhood Reinvestment Initiative · Impact framework
              </span>
              <br />
              Resident-level evidence for each goal, across all priority neighborhoods
            </span>
          }
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/reports")}
              >
                <ChevronLeft />
                Reports
              </Button>
              <Button
                type="button"
                variant="brand-outline"
                onClick={() => window.print()}
              >
                <ExternalLink />
                Export report
              </Button>
            </div>
          }
        />
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[
            ["Sign up", "Residents who joined", "3,490", "64% are legacy residents"],
            ["Show up", "Came to at least one activity", "2,722", "78% of members"],
            ["Speak up", "Resident inputs on projects", "3,680", "105 per 100 members"],
            ["Result", "Say they're better off than a year ago", "40%", "▲ 9 pts since spring"],
          ].map(([eyebrow, label, value, note]) => (
            <Card
              className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm"
              key={eyebrow}
            >
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#008a72]">
                {eyebrow}
              </p>
              <p className="mt-3 text-sm font-bold text-[#587268]">{label}</p>
              <strong className="mt-2 block text-3xl">{value}</strong>
              <span className="mt-2 block text-xs font-semibold text-[#007b67]">{note}</span>
            </Card>
          ))}
        </section>
        <section className="grid gap-6 xl:grid-cols-3">
          {nriImpactGoals.map((goal, index) => (
            <Card
              className="overflow-hidden rounded-3xl border border-[#dbe9e1] bg-white shadow-sm"
              key={goal.title}
            >
              <div className="bg-[#004b3a] p-5 text-white">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#f9c75c]">
                  Goal {index + 1}
                </p>
                <h2 className="mt-2 text-xl font-bold">{goal.title}</h2>
                <p className="mt-1 text-sm text-[#d8efe5]">{goal.question}</p>
              </div>
              <div className="space-y-5 p-5">
                {goal.metrics.map(([label, value, progress]) => (
                  <article key={label}>
                    <div className="flex justify-between gap-3 text-sm font-bold">
                      <span>{label}</span>
                      <span>{value}</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#e4f0e8]">
                      <i
                        className="block h-full rounded-full bg-[#00c49a]"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </article>
                ))}
                <aside className="rounded-2xl bg-[#fff0cf] p-3 text-sm font-bold text-[#805200]">
                  <span className="block text-xs uppercase tracking-[0.08em]">{goal.tone}</span>
                  {goal.note}
                </aside>
              </div>
            </Card>
          ))}
        </section>
        <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-xl font-bold">The 8 Building Blocks for a Thriving Neighborhood</h2>
          <p className="mt-2 text-sm text-[#587268]">
            Residents participate and input are sorted into the city's priority categories.
          </p>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {nriBuildingBlocks.map(([title, tookPart, inputs, status]) => (
              <article
                className="rounded-2xl border border-[#dbe9e1] bg-[#f7fcf8] p-4"
                key={title}
              >
                <h3 className="font-bold">{title}</h3>
                <div className="mt-4 flex gap-6">
                  <span>
                    <strong className="block text-xl">{tookPart}</strong>
                    <small className="text-[#587268]">took part</small>
                  </span>
                  <span>
                    <strong className="block text-xl">{inputs}</strong>
                    <small className="text-[#587268]">inputs</small>
                  </span>
                </div>
                <span className="mt-4 inline-block rounded-full bg-[#d4f5ea] px-3 py-1 text-xs font-bold text-[#006b58]">
                  {status}
                </span>
              </article>
            ))}
          </div>
        </Card>
        <section className="grid gap-6 xl:grid-cols-[minmax(0,.65fr)_minmax(0,1fr)]">
          <section className="rounded-3xl bg-[#004b3a] p-6 text-white">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#f9c75c]">
              The Mayor's North Star
            </p>
            <h2 className="mt-3 text-2xl font-bold">
              Making Atlanta the best place in the country to raise a child
            </h2>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                ["40%", "of member households have children"],
                ["445", "youth members, ages 13 to 17"],
                ["9,790", "youth program visits this year"],
                ["489", "parents who also gave input"],
              ].map(([value, label]) => (
                <article
                  className="rounded-2xl bg-[#00664f] p-4"
                  key={label}
                >
                  <strong className="block text-2xl">{value}</strong>
                  <span className="text-xs text-[#d8efe5]">{label}</span>
                </article>
              ))}
            </div>
          </section>
          <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-bold">What residents want built</h2>
            <div className="mt-5 space-y-4">
              {[
                ["Grocery store nearby", "812"],
                ["Affordable homes for current residents", "744"],
                ["Safer streets and lighting", "610"],
                ["Transit to job centers", "455"],
                ["Better schools and youth programs", "398"],
              ].map(([label, value]) => (
                <div
                  className="grid grid-cols-[minmax(0,1fr)_160px_40px] items-center gap-3"
                  key={label}
                >
                  <strong className="text-sm">{label}</strong>
                  <div className="h-2 overflow-hidden rounded-full bg-[#e4f0e8]">
                    <i
                      className="block h-full rounded-full bg-[#00c49a]"
                      style={{ width: `${Math.round(Number(value) / 9)}%` }}
                    />
                  </div>
                  <span className="text-right text-sm font-bold">{value}</span>
                </div>
              ))}
            </div>
          </Card>
        </section>
        <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-xl font-bold">Resident outcome check-in</h2>
          <p className="mt-2 text-sm text-[#587268]">
            Short survey twice a year · change among the same member cohort
          </p>
          <div className="mt-5 space-y-5">
            {nriOutcomeChecks.map(([label, current, prior, change]) => (
              <article
                className="grid gap-3 md:grid-cols-[280px_minmax(0,1fr)_90px] md:items-center"
                key={label}
              >
                <div>
                  <strong>{label}</strong>
                  <p className="text-xs text-[#587268]">Fall 2026 compared with Spring 2026</p>
                </div>
                <div>
                  <div className="h-2 overflow-hidden rounded-full bg-[#d6eadc]">
                    <i
                      className="block h-full rounded-full bg-[#008a72]"
                      style={{ width: current }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-[#587268]">
                    Spring {prior} · Fall {current}
                  </p>
                </div>
                <span className="text-sm font-bold text-[#007b67]">{change}</span>
              </article>
            ))}
          </div>
        </Card>
        <Card className="rounded-3xl border border-[#dbe9e1] bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-xl font-bold">All 8 priority neighborhood areas</h2>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[850px] text-left text-sm">
              <thead className="border-b text-xs uppercase text-[#587268]">
                <tr>
                  <th className="pb-3">Area</th>
                  <th className="pb-3">Sign up</th>
                  <th className="pb-3">Legacy</th>
                  <th className="pb-3">Speak up</th>
                  <th className="pb-3">Got help</th>
                  <th className="pb-3">Leaders</th>
                  <th className="pb-3">Still here</th>
                  <th className="pb-3">Better off</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf3ef]">
                {nriPriorityAreas.map((area) => (
                  <tr key={area[0]}>
                    {area.map((value, index) => (
                      <td
                        className={`py-4 ${index === 0 ? "font-bold" : "font-semibold"}`}
                        key={value}
                      >
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </main>
  );
}
